import type { ReactNode } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import {
    Autocomplete,
    Box,
    Button,
    FormControlLabel,
    InputAdornment,
    Paper,
    Stack,
    Switch,
    TextField,
} from '@mui/material';
import { useConfigurationContext } from '@app/root/ConfigurationContext';
import type { BaseInventoryByAntennaFilters } from './inventoryByAntennaFilters';

type InventoryByAntennaFilterToolbarProps = {
    rowSearchLabel: string;
    rowSearchPlaceholder: string;
    filters: BaseInventoryByAntennaFilters;
    onFiltersChange: (filters: BaseInventoryByAntennaFilters) => void;
    extraFilters?: ReactNode;
    hasActiveFilters: boolean;
    onClearFilters: () => void;
};

export const InventoryByAntennaFilterToolbar = ({
    rowSearchLabel,
    rowSearchPlaceholder,
    filters,
    onFiltersChange,
    extraFilters,
    hasActiveFilters,
    onClearFilters,
}: InventoryByAntennaFilterToolbarProps) => {
    const { antennaTypes } = useConfigurationContext();

    const selectedAntennaTypes = antennaTypes.filter(type =>
        filters.antennaTypes.includes(type.value)
    );

    return (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
            <Stack
                direction="row"
                flexWrap="wrap"
                useFlexGap
                spacing={1.5}
                alignItems="center"
            >
                <TextField
                    size="small"
                    label={rowSearchLabel}
                    placeholder={rowSearchPlaceholder}
                    value={filters.rowSearch}
                    onChange={event =>
                        onFiltersChange({
                            ...filters,
                            rowSearch: event.target.value,
                        })
                    }
                    sx={{ minWidth: 220, flex: '1 1 220px' }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
                <TextField
                    size="small"
                    label="Antennas"
                    placeholder="Search antenna name..."
                    value={filters.antennaSearch}
                    onChange={event =>
                        onFiltersChange({
                            ...filters,
                            antennaSearch: event.target.value,
                        })
                    }
                    sx={{ minWidth: 220, flex: '1 1 220px' }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
                <Autocomplete
                    multiple
                    size="small"
                    options={antennaTypes}
                    value={selectedAntennaTypes}
                    onChange={(_event, value) =>
                        onFiltersChange({
                            ...filters,
                            antennaTypes: value.map(type => type.value),
                        })
                    }
                    getOptionLabel={option => option.label}
                    isOptionEqualToValue={(option, value) =>
                        option.value === value.value
                    }
                    renderInput={params => (
                        <TextField
                            {...params}
                            label="Antenna type"
                            placeholder="All types"
                        />
                    )}
                    sx={{ minWidth: 220, flex: '1 1 220px' }}
                />
                {extraFilters}
                <FormControlLabel
                    control={
                        <Switch
                            size="small"
                            checked={filters.nonZeroStockOnly}
                            onChange={(_event, checked) =>
                                onFiltersChange({
                                    ...filters,
                                    nonZeroStockOnly: checked,
                                })
                            }
                        />
                    }
                    label="Non-zero stock only"
                    sx={{ ml: 0.5, flexShrink: 0 }}
                />
                <Box flexGrow={1} />
                <Button
                    size="small"
                    variant="outlined"
                    disabled={!hasActiveFilters}
                    onClick={onClearFilters}
                >
                    Clear all
                </Button>
            </Stack>
        </Paper>
    );
};
